import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { c as createTypeValidator, a as createLogger, e as emitEvent, f as isUnset, u as updateElementStyle, d as isNumber, l as logDeprecatedEventOnce } from './utils.js';
import { d as defineCustomElement$4 } from './cl-icon2.js';
import { d as defineCustomElement$3 } from './cl-icon-button2.js';
import { d as defineCustomElement$2 } from './cl-typography2.js';

// Constants
const component = 'cl-dialog';
const DIALOG_SIZE = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
};
const dialogConstants = {
  ELEVATION: {
    0: 0,
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
  },
  FONT_SIZE: {
    [DIALOG_SIZE.SMALL]: {
      header: 'h6',
      subheader: 'subtitle-1',
    },
    [DIALOG_SIZE.MEDIUM]: {
      header: 'h4',
      subheader: 'h6',
    },
    [DIALOG_SIZE.LARGE]: {
      header: 'h3',
      subheader: 'introtext',
    },
  },
  SIZE: DIALOG_SIZE,
};
// Type-validators
const dialogValidator = {
  elevation: createTypeValidator(dialogConstants.ELEVATION),
  size: createTypeValidator(dialogConstants.SIZE),
};

const clDialogCss = ".sc-cl-dialog-h{--dialog-content-width:fit-content;background-color:rgba(var(--color-neutral-main));border-radius:var(--border-radius-large);box-shadow:var(--custom-dialog-elevation, var(--dialog-elevation, var(--elevation-2)));display:flex;padding:var(--spacing-6);position:relative;width:-moz-fit-content;width:fit-content;z-index:var(--custom-dialog-z-index, var(--dialog-z-index, var(--z-index-6)))}.sc-cl-dialog-h,.sc-cl-dialog-h *.sc-cl-dialog,.sc-cl-dialog-h *.sc-cl-dialog::before,.sc-cl-dialog-h *.sc-cl-dialog::after,.sc-cl-dialog-h .sc-cl-dialog-s>*,.sc-cl-dialog-h .sc-cl-dialog-s *,.sc-cl-dialog-h .sc-cl-dialog-s *::before,.sc-cl-dialog-h .sc-cl-dialog-s *::after{box-sizing:border-box}.dialog--vertical.sc-cl-dialog-h{display:block}.dialog.sc-cl-dialog-h .dialog__pictogram.sc-cl-dialog{margin:0 var(--spacing-5) 0 0}.dialog.sc-cl-dialog-h .dialog__content.sc-cl-dialog{margin:var(--spacing-3) 0 0 0}.sc-cl-dialog-h.dialog .sc-cl-dialog-s>[slot=pictogram]{height:calc(28 * var(--spacing-1));margin:0 auto;width:calc(28 * var(--spacing-1))}.dialog--vertical.sc-cl-dialog-h{text-align:center}.dialog--vertical.sc-cl-dialog-h .dialog__pictogram.sc-cl-dialog{margin:0 0 var(--spacing-2) 0}.sc-cl-dialog-h.dialog--vertical .sc-cl-dialog-s>[slot=actions]{text-align:center}.dialog--vertical.dialog--large.sc-cl-dialog-h .dialog__pictogram.sc-cl-dialog{margin:0 0 var(--spacing-3) 0}.dialog--medium.sc-cl-dialog-h .dialog__content__actions.sc-cl-dialog,.dialog--large.sc-cl-dialog-h .dialog__content__actions.sc-cl-dialog{margin-top:var(--spacing-5)}.dialog--opening.sc-cl-dialog-h{animation:open 100ms var(--animation-timing-easeinout)}.dialog--opened.sc-cl-dialog-h{opacity:1}.dialog--closing.sc-cl-dialog-h{animation:close 100ms var(--animation-timing-easeinout)}.dialog--closed.sc-cl-dialog-h{display:none;opacity:0}.sc-cl-dialog-h .dialog__close.sc-cl-dialog{--icon-size:var(--spacing-3);--icon-color:rgba(var(--color-neutral-900));cursor:pointer;position:absolute;right:var(--spacing-3);top:var(--spacing-3)}.sc-cl-dialog-h .dialog__content.sc-cl-dialog{width:var(--dialog-content-width)}.sc-cl-dialog-h .dialog__content__header.sc-cl-dialog{margin:0 0 var(--spacing-1) 0}.sc-cl-dialog-h .dialog__content__subheader.sc-cl-dialog{margin:0 0 var(--spacing-3) 0}.sc-cl-dialog-h .dialog__content__actions.sc-cl-dialog{margin-top:var(--spacing-4)}@keyframes open{from{opacity:0}to{opacity:1}}@keyframes close{from{opacity:1}to{opacity:0}}";

const ClDialog$1 = /*@__PURE__*/ proxyCustomElement(class ClDialog extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.clCloseEnd = createEvent(this, "clCloseEnd", 7);
    this.clCloseStart = createEvent(this, "clCloseStart", 7);
    this.clOpenEnd = createEvent(this, "clOpenEnd", 7);
    this.clOpenStart = createEvent(this, "clOpenStart", 7);
    this.closeEnd = createEvent(this, "closeEnd", 7);
    this.closeStart = createEvent(this, "closeStart", 7);
    this.openEnd = createEvent(this, "openEnd", 7);
    this.openStart = createEvent(this, "openStart", 7);
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * Whether the dialog is being animated or not
     */
    this.isAnimating = false;
    /**
     * Handler for the "close" method
     */
    this.bindClose = this.closeEl.bind(this);
    /**
     * Emits the `closed` event
     */
    this.closeDialog = () => {
      if (!this.isAnimating) {
        this.bindClose();
      }
    };
    /**
     * Returns a pictogram if one was placed in the `pictogram` slot
     */
    this.getPictogram = () => {
      if (!this.hasPictogram)
        return;
      return (h("div", { class: 'dialog__pictogram' }, h("slot", { name: 'pictogram' })));
    };
    /**
     * Function that emits the correct animation event based on whether the dialog is opening or closing
     */
    this.handleAnimationEvent = (event) => {
      const { animationName, type } = event;
      const isOpenEvent = animationName === 'open';
      const isCloseEvent = animationName === 'close';
      switch (true) {
        case isOpenEvent && type === 'animationstart':
          emitEvent(this, 'clOpenStart', { component, originalEvent: event }, null);
          break;
        case isOpenEvent && type === 'animationend':
          this.element.classList.remove('dialog--opening');
          this.element.classList.add('dialog--opened');
          this.isAnimating = false;
          emitEvent(this, 'clOpenEnd', { component, originalEvent: event }, null);
          break;
        case isCloseEvent && type === 'animationstart':
          emitEvent(this, 'clCloseStart', { component, originalEvent: event }, null);
          break;
        case isCloseEvent && type === 'animationend':
          this.element.classList.remove('dialog--closing');
          this.element.classList.add('dialog--closed');
          this.isAnimating = false;
          emitEvent(this, 'clCloseEnd', { component, originalEvent: event }, null);
          break;
      }
    };
    this.elevation = 2;
    this.header = '';
    this.open = true;
    this.vertical = false;
    this.size = 'medium';
    this.subheader = '';
    this.zIndex = 10;
  }
  /**
   * Validates the `elevation` property and updates the styles accordingly
   */
  handleElevation(elevation) {
    const cssVariable = '--custom-dialog-elevation';
    if (isUnset(elevation)) {
      updateElementStyle(this.element, undefined, cssVariable);
    }
    else if (isNumber(elevation)) {
      if (!dialogValidator.elevation(+elevation)) {
        this.logger.error.attribute.elevation(`Given invalid value "${elevation}".`);
      }
      else {
        updateElementStyle(this.element, `var(--elevation-${elevation})`, cssVariable);
      }
    }
  }
  /**
   * Updates the dialog element styles based on the `open` property
   */
  handleOpen(isOpen, wasOpen) {
    switch (true) {
      case isOpen && !wasOpen: {
        this.element.classList.remove('dialog--closing');
        this.element.classList.remove('dialog--closed');
        this.element.classList.add('dialog--opening');
        this.isAnimating = true;
        break;
      }
      case !isOpen && wasOpen: {
        this.element.classList.remove('dialog--opening');
        this.element.classList.remove('dialog--opened');
        this.element.classList.add('dialog--closing');
        this.isAnimating = true;
        break;
      }
      case !isOpen && !wasOpen: {
        this.element.classList.remove('dialog--opened');
        this.element.classList.add('dialog--closed');
        break;
      }
    }
  }
  /**
   * Validates the size property
   */
  handleSize(size) {
    if (!dialogValidator.size(size)) {
      this.logger.error.attribute.size(`Given invalid value "${size}".`);
    }
  }
  /**
   * Validates the `zIndex` property and updates the styles accordingly
   */
  updateZIndex(zIndex) {
    const cssVariable = '--custom-dialog-z-index';
    switch (true) {
      case isUnset(zIndex):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case !isNumber(zIndex):
        this.logger.error.attribute.zIndex(`Given invalid value "${zIndex}".`);
        break;
      default:
        updateElementStyle(this.element, `${zIndex}`, cssVariable);
        break;
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Console log deprecation warnings;
   * - Apply the open/close styling;
   * - Validate the size attribute;
   */
  connectedCallback() {
    logDeprecatedEventOnce(this.logger, 'closeEnd', 'clCloseEnd');
    logDeprecatedEventOnce(this.logger, 'closeStart', 'clCloseStart');
    logDeprecatedEventOnce(this.logger, 'openEnd', 'clOpenEnd');
    logDeprecatedEventOnce(this.logger, 'openStart', 'clOpenStart');
    this.handleOpen(this.open);
    this.handleSize(this.size);
  }
  /**
   * Just once before the component has fully loaded and rendered:
   * - Check whether a slotted pictogram element is set;
   * - Check whether slotted actions are set;
   * - Validate the z=index attribute and update the styles;
   * - Validate the elevation attribute and update the styles;
   */
  componentWillLoad() {
    this.hasPictogram = !!this.element.querySelector('[slot="pictogram"]');
    this.hasActions = !!this.element.querySelector('[slot="actions"]');
    this.updateZIndex(this.zIndex);
    this.handleElevation(this.elevation);
  }
  /**
   * A method to open the dialog.
   */
  async openEl() {
    this.open = true;
  }
  /**
   * A method to close the dialog.
   */
  async closeEl() {
    this.open = false;
  }
  render() {
    const { header, size, subheader, vertical } = this;
    return (h(Host, { class: cl(`dialog dialog--${size}`, { 'dialog--vertical': vertical }), onAnimationEnd: this.handleAnimationEvent, onAnimationStart: this.handleAnimationEvent }, h("cl-icon-button", { class: 'dialog__close', "icon-name": 'action--close', variant: 'tertiary', onClick: this.closeDialog }), this.getPictogram(), h("div", { class: 'dialog__content' }, header && (h("cl-typography", { class: 'dialog__content__header', variant: dialogConstants.FONT_SIZE[size].header }, header)), subheader && (h("cl-typography", { class: 'dialog__content__subheader', variant: dialogConstants.FONT_SIZE[size].subheader }, subheader)), h("slot", { name: 'content' }), this.hasActions && (h("div", { class: 'dialog__content__actions' }, h("slot", { name: 'actions' }))))));
  }
  get element() { return this; }
  static get watchers() { return {
    "elevation": ["handleElevation"],
    "open": ["handleOpen"],
    "size": ["handleSize"],
    "zIndex": ["updateZIndex"]
  }; }
  static get style() { return clDialogCss; }
}, [6, "cl-dialog", {
    "elevation": [514],
    "header": [1],
    "open": [1028],
    "vertical": [4],
    "size": [1],
    "subheader": [1],
    "zIndex": [514, "z-index"],
    "openEl": [64],
    "closeEl": [64]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-dialog", "cl-icon", "cl-icon-button", "cl-typography"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-dialog":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClDialog$1);
      }
      break;
    case "cl-icon":
      if (!customElements.get(tagName)) {
        defineCustomElement$4();
      }
      break;
    case "cl-icon-button":
      if (!customElements.get(tagName)) {
        defineCustomElement$3();
      }
      break;
    case "cl-typography":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
  } });
}

const ClDialog = ClDialog$1;
const defineCustomElement = defineCustomElement$1;

export { ClDialog, defineCustomElement };

//# sourceMappingURL=cl-dialog.js.map